import * as React from "react"
import { useEffect } from "react"
import { Route, Switch } from "react-router-dom"
import { useUpdateEffect } from "react-use"
import { type ApolloClient, ApolloProvider, type NormalizedCacheObject } from "@apollo/client"
import { ModuleLoading } from "@digits-shared/components/Loaders"
import { ProtectedRoute } from "@digits-shared/components/Router/ProtectedRoute"
import envHelper from "@digits-shared/helpers/envHelper"
import useConstant from "@digits-shared/hooks/useConstant"
import useSession from "@digits-shared/hooks/useSession"
import { useSessionListeners } from "@digits-shared/session/useSessionListeners"
import {
  type ApolloClientOptions,
  setupApolloClient,
} from "src/shared/initializers/apollo/setupApolloClient"
import { AdminBearerRefresh } from "src/admin/components/Session/AdminBearerRefresh"
import LogOut from "src/admin/components/Session/LogOut"
import routes from "src/admin/routes"
import type AdminSession from "src/admin/session/AdminSession"
import ClientVersionTracker from "src/shared/components/Background/ClientVersionTracker"
import { TimeContextURLProvider } from "src/shared/components/Contexts/TimeContext"
import { DeveloperToolsGate } from "src/shared/components/DeveloperTools/DeveloperToolsGate"

const DeveloperTools = import.meta.env.DEVELOPER_TOOLS_ENABLED
  ? React.lazy(() => import("src/admin/components/DeveloperTools/AdminDeveloperTools"))
  : () => null

const APOLLO_CLIENT_OPTIONS: ApolloClientOptions = {
  defaultFetchPolicy: "network-only",
  neverBatch: true,
  withWS: true,
}

/*
  COMPONENTS
*/

const LogIn = React.lazy(() => import("src/admin/components/Session/LogIn"))

const Home = React.lazy(() => import("src/admin/components/Home/Home"))

/**
 * Top-level app component for the admin portal
 */
export const AdminClient: React.FC = () => {
  useSessionListeners(routes.login.parameterizedPath)
  const session = useSession<AdminSession>()

  const initialClient = useConstant(() => setupApolloClient(session, APOLLO_CLIENT_OPTIONS))
  const [apolloClient, setApolloClient] =
    React.useState<ApolloClient<NormalizedCacheObject>>(initialClient)

  useUpdateEffect(() => {
    apolloClient.stop()
    setApolloClient(setupApolloClient(session, APOLLO_CLIENT_OPTIONS))
  }, [session.user])

  useEffect(() => {
    let title = "Digits Admin"

    if (envHelper.isDevelopment()) {
      title = "[Dev] " + title
    } else if (envHelper.isStaging()) {
      title = "[Staging] " + title
    }

    document.title = title
  }, [])

  return (
    <ApolloProvider client={apolloClient}>
      {import.meta.env.DEVELOPER_TOOLS_ENABLED && (
        <DeveloperToolsGate>
          <DeveloperTools />
        </DeveloperToolsGate>
      )}
      <TimeContextURLProvider routes={routes}>
        <AdminBearerRefresh />
        <ClientVersionTracker />
        <React.Suspense fallback={<ModuleLoading />}>
          <Switch>
            <Route exact path={routes.login.parameterizedPath} component={LogIn} />

            <Route
              exact
              path={routes.logout.parameterizedPath}
              render={() => (
                <ProtectedRoute component={LogOut} redirectTo={routes.login.parameterizedPath} />
              )}
            />

            <Route
              path="*"
              render={() => (
                <ProtectedRoute component={Home} redirectTo={routes.login.parameterizedPath} />
              )}
            />
          </Switch>
        </React.Suspense>
      </TimeContextURLProvider>
    </ApolloProvider>
  )
}
